﻿import React, { useState } from 'react';
import { useStateContext } from '../../contexts/ContextProvider';
import { Header } from '../../components';
import maleBodyImage from '../../images/whole-body-figures/body-image-male.png';
import femaleBodyImage from '../../images/whole-body-figures/body-image-female.png';

const painIntensityColors = [
	"#00FF00", "#80FF00", "#C3FFFF", "#94FFB9", "#DDFF46",
	"#FFFF01", "#FFCF00", "#FFB200", "#FF8A02", "#FF6C02", "#FF0000"
];





const BodyPainVisualizer = () => {
	const { currentMode } = useStateContext();
	const [spotSize, setSpotSize] = useState(5);
	const [selectedColor, setSelectedColor] = useState("");
	const painLevels = [
		{ level: 0, label: "No Pain" },
		{ level: 1, label: "Very Mild" },
		{ level: 2, label: "Mild" },
		{ level: 3, label: "Mild+" },
		{ level: 4, label: "Moderate" },
		{ level: 5, label: "Moderate+" },
		{ level: 6, label: "Mod. Severe" },
		{ level: 7, label: "Severe" },
		{ level: 8, label: "Very Severe" },
		{ level: 9, label: "Nearly Unbearable" },
		{ level: 10, label: "Worst Possible" }
	];
	const [painSpots, setPainSpots] = useState([]);
	const [undoHistory, setUndoHistory] = useState([]);
	const [gender, setGender] = useState('male');

	const getBodyImage = () => {
		return gender === 'male' ? maleBodyImage : femaleBodyImage;
	};

	const handleColorSelect = (color) => {
		setSelectedColor(color);
	};

	const handleBodyClick = (event) => {
		if (!selectedColor) {
			alert('Please select a pain intensity color first');
			return;
		}

		const rect = event.currentTarget.getBoundingClientRect();
		const x = ((event.clientX - rect.left) / rect.width) * 100;
		const y = ((event.clientY - rect.top) / rect.height) * 100;

		const newSpot = {
			id: Date.now(),
			pos_X: x,
			pos_Y: y,
			diameter: spotSize, // Use the spotSize state instead of hardcoded value
			color: selectedColor,
			opacity: 0.5
		};

		setPainSpots([...painSpots, newSpot]);
		setUndoHistory([]);
	};

	const handleUndo = () => {
		if (painSpots.length === 0) return;

		const lastSpot = painSpots[painSpots.length - 1];
		const newPainSpots = painSpots.slice(0, -1);

		setUndoHistory([...undoHistory, lastSpot]);
		setPainSpots(newPainSpots);
	};

	const handleRedo = () => {
		if (undoHistory.length === 0) return;

		const lastUndo = undoHistory[undoHistory.length - 1];
		const newUndoHistory = undoHistory.slice(0, -1);

		setPainSpots([...painSpots, lastUndo]);
		setUndoHistory(newUndoHistory);
	};

	const handleClear = () => {
		setPainSpots([]);
		setUndoHistory([]);
	};

	const toggleGender = () => {
		setGender(gender === 'male' ? 'female' : 'male');
	};

	return (
		<div className={`w-full ${currentMode === 'Dark' ? 'dark' : ''}`}>
			<div className="flex flex-col w-full">
                {/* Gender Toggle and Controls */}
                <div className="mb-4 flex justify-between items-center">
                        <div className="flex space-x-4">
                                <button
                                        type="button"
                                        onClick={handleUndo}
                                        disabled={painSpots.length === 0}
                                        className="btn btn-primary btn-sm"
                                >
                                        Undo
                                </button>
                                <button
                                        type="button"
                                        onClick={handleRedo}
                                        disabled={undoHistory.length === 0}
                                        className="btn btn-primary btn-sm"
                                >
                                        Redo
                                </button>
                                <button
                                        type="button"
                                        onClick={handleClear}
                                        disabled={painSpots.length === 0}
                                        className="btn btn-error btn-sm"
                                >
                                        Clear
                                </button>
                        </div>
                        <button
                                type="button"
                                onClick={toggleGender}
                                className="btn btn-secondary btn-sm"
                        >
                                Switch to {gender === 'male' ? 'Female' : 'Male'} Body
                        </button>
                </div>				{/* Color Selection Bar */}
				<div className="mb-6 bg-base-100 p-4 rounded-lg shadow border border-base-300">
					<h3 className="text-lg font-semibold mb-4 text-base-content">VAS Pain Scale (0-10)</h3>
					<div className="grid grid-cols-11 gap-2">
						{painIntensityColors.map((color, index) => (
							<button
								type="button"
								key={color}
								className={`h-12 rounded-lg border-2 transition-all text-xs font-bold ${
									selectedColor === color ? 'border-blue-500 shadow-lg scale-110' : 'border-gray-300'
								}`}
								style={{ 
									backgroundColor: color,
									color: index > 6 ? 'white' : 'black'
								}}
								onClick={() => handleColorSelect(color)}
								title={`Level ${index}: ${painLevels[index]?.label}`}
							>
								{index}
							</button>
						))}
					</div>
					<div className="grid grid-cols-3 mt-3 text-sm text-center">
						<div className="text-green-600 font-medium">0-3: Mild Pain</div>
						<div className="text-yellow-600 font-medium">4-6: Moderate Pain</div>
						<div className="text-red-600 font-medium">7-10: Severe Pain</div>
					</div>
				</div>

                {/* Pain Spot Size Slider */}
                <div className="mb-6 bg-base-100 p-4 rounded-lg shadow border border-base-300">
                        <div className="flex items-center space-x-4">
                                <label className="text-sm font-medium min-w-[80px] text-base-content">Pain size:</label>
                                <input
                                        type="range"
                                        min="2"
                                        max="10"
                                        value={spotSize}
                                        onChange={(e) => setSpotSize(Number(e.target.value))}
                                        className="flex-grow h-2 rounded-lg appearance-none cursor-pointer bg-gray-200"
                                />
                                <span className="text-sm w-12 text-center">{spotSize}</span>
                        </div>
                </div>				{/* Body Image Container */}
				                {/* Body Image Container */}
                <div className="bg-base-100 rounded-lg shadow-lg p-6 border border-base-300">
					<div className="relative w-full max-w-md mx-auto" onClick={handleBodyClick}>
						{/* Base Image */}
						<img
							src={getBodyImage()}
							alt={`${gender} body outline`}
							className="w-full h-auto"
						/>

						{/* Pain Spots Overlay */}
						<div className="absolute inset-0">
							<svg className="w-full h-full" style={{ pointerEvents: 'none' }}>
								{painSpots.map((spot) => (
									<g key={spot.id}>
										<circle
											cx={`${spot.pos_X}%`}
											cy={`${spot.pos_Y}%`}
											r={`${spot.diameter}%`}
											fill={spot.color}
											fillOpacity={spot.opacity}
											stroke="black"
											strokeOpacity="0.3"
										/>
										<circle
											cx={`${spot.pos_X}%`}
											cy={`${spot.pos_Y}%`}
											r={`${spot.diameter - 0.5}%`}
											fill="white"
											fillOpacity="0.3"
										/>
									</g>
								))}
							</svg>
						</div>
					</div>
				</div>

				{/* Pain Level Description */}
				<div className="mt-6 bg-base-100 p-4 rounded-lg shadow border border-base-300">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div className="p-4 border border-success rounded bg-success/10">
							<h4 className="font-semibold mb-2 text-success">Mild Pain (1-3)</h4>
							<p className="text-sm text-base-content">Pain doesn't interfere with daily activities</p>
						</div>
						<div className="p-4 border border-warning rounded bg-warning/10">
							<h4 className="font-semibold mb-2 text-warning">Moderate Pain (4-6)</h4>
							<p className="text-sm text-base-content">Pain interferes with some activities</p>
						</div>
						<div className="p-4 border border-error rounded bg-error/10">
							<h4 className="font-semibold mb-2 text-error">Severe Pain (7-10)</h4>
							<p className="text-sm text-base-content">Pain interferes with most/all activities</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BodyPainVisualizer;